// mixin wrapper around deriveAppMeta pure helper.
// Bridges prop `app` OR `mixin_app` (from app-installation mixin) into a unified
// computed metadata object that templates can use. Future Vue 3 migration: turn
// this into a composable `useAppMeta(appRef)` returning the same shape.

import { deriveAppMeta } from '@/js/app-derived.js';

export default {
  computed: {
    // prefer explicit prop-provided app if present, else fallback to the mixin-managed one.
    current_app() {
      return this.app || this.mixin_app || null;
    },

    app_meta() {
      return deriveAppMeta(this.current_app);
    },

    // enum label preferred if available
    app_label() {
      const app = this.current_app;
      if (!app) return '';
      return this.$rc.getAppLabelFromId(app.app_id) || app.app_label || 'Unknown App';
    },

    // convenience alias computed for existing template expectations or ergonomic use.
    app_is_installed() {
      return this.app_meta.is_installed;
    },

    app_is_update_eligible() {
      return this.app_meta.is_update_eligible;
    },

    app_price_is_set() {
      return this.app_meta.price_is_set;
    },

    app_is_free() {
      return this.app_meta.is_free;
    },

    app_has_free_trial() {
      return this.app_meta.has_free_trial;
    },

    app_is_approved() {
      const app = this.current_app;
      if (!app) return false;
      return (app.state || '').toLowerCase() === 'approved';
    },

    app_monthly_fee_pretty() {
      if (!this.app_price_is_set) return '';
      // re-use existing pretty helper if available (from app-installation mixin) else manual.
      if (typeof this.mixinMonthlyFeePretty === 'function') {
        const value = this.app_meta.monthly_fee_millicents;
        return this.mixinMonthlyFeePretty(value);
      }
      const millicents = this.app_meta.monthly_fee_millicents;
      if (millicents == null) return '';
      return millicents > 0 ? `${this.$rc.pretty(millicents, 'usd')}/mo` : 'Free';
    },

    app_trial_end_date_pretty() {
      const app = this.current_app;
      if (!app) return '';

      if (app.free_trial_days_remaining && app.free_trial_days_remaining > 0) {
        const today = new Date();
        const end_date = new Date(
          today.getTime() + app.free_trial_days_remaining * 24 * 60 * 60 * 1000
        );
        return this.$rc.pretty(end_date, 'date');
      }

      if (app.trial_end_date) {
        return this.$rc.pretty(app.trial_end_date, 'date');
      }
      return '';
    },

    app_is_in_free_trial() {
      const app = this.current_app;
      if (!app) return false;
      return this.app_is_installed && this.app_has_free_trial && !this.app_is_free;
    },

    app_trial_status_message() {
      const app = this.current_app;
      if (!app) return '';

      const end_date = this.app_trial_end_date_pretty;

      // no remaining days -> past or ends today, still show end date if exists
      const days_remaining = app.free_trial_days_remaining;

      if (!days_remaining || days_remaining <= 0) {
        if (!end_date) return '';
        return `Free trial ends on ${end_date}`;
      }
      if (days_remaining === 1) {
        return `Free trial ends tomorrow (${end_date})`;
      } else if (days_remaining <= 7) {
        return `Free trial ends in ${days_remaining} days (${end_date})`;
      }
      return `Free trial ends on ${end_date}`;
    },
  },
};
